// Birim varyantı DR3 — ForkSetModal "Birim tercihleri" adımının saf mantığı. Seçilen göstergeler
// birim ailesine göre gruplanır: para (`currencyBaseOf` — `unitFamilyOf` para taşımaz), çevrim
// tablosu aileleri (`unitFamilyOf`), aileye girmeyen ama varyantlı gösterge kendi satırında.
// Aile satırı TOPLU KISAYOLDUR: yalnız ORTAK geçerli seçenekler (her göstergenin seçenek kümesinin
// kesişimi: varsayılan ∪ varyantlar ∪ çevrilebilir komşular ∪ para ölçekleri). Seçimi alamayan ya da
// müşteride zaten kayıtlı birimi olan gösterge İSTİSNA olur ve kendi birimini korur (sunucu da
// mevcut müşteri kaydını tercihle ezmez). Gövdeye yalnız varsayılandan farklı seçimler gider.
// Birim karşılaştırması her yerde `normalizeUnitKey` (TRY ≡ TL); "" = birimsiz.

import {
  KPI_UNIT_FAMILIES,
  currencyBaseOf,
  currencyScaleUnits,
  normalizeUnitKey,
  reachableUnits,
  unitFamilyOf,
  type KpiMetric,
} from "@mahir/shared";
import { KPI_UNIT_COPY } from "./kpi-unit-copy";

export interface UnitPrefMetric {
  metricId: string;
  label: string;
  /** katalog varsayılanı ("" = birimsiz) */
  defaultUnit: string;
  variants: string[];
  /** müşterinin mevcut kalıcı birimi (varsa satır kilitli DEĞİL, yalnız bu gösterge sabit) */
  clientUnit: string | null;
  /**
   * Sektör paketi `unit_default` ön seçimi (yalnız müşteri kaydı yokken ve birim göstergenin
   * geçerli seçeneklerindeyse) — görünümde "paket varsayılanı" rozeti.
   */
  packageUnit: string | null;
  /** göstergenin kendi geçerli seçenekleri */
  options: string[];
  /** kurulumda alacağı birim */
  unit: string;
}

export interface UnitFamilyRow {
  /** "currency" | aile anahtarı | "metric:<id>" */
  key: string;
  label: string;
  metricIds: string[];
  /** ORTAK geçerli seçenekler (aile kısayolu yalnız bunları sunar) */
  options: string[];
  choice: string;
  metrics: UnitPrefMetric[];
  exceptions: { metricId: string; unit: string }[];
}

const keyOf = (u: string): string => (u.trim() === "" ? "" : normalizeUnitKey(u));
const same = (a: string, b: string): boolean => keyOf(a) === keyOf(b);
/** Birim eşitliği (normalize anahtar; "" = birimsiz) — görünüm de bunu kullanır. */
export const sameUnit = same;

/**
 * TODO-KPI-97: kullanımda (değer/hedef) olan ve seçilen birimi etkin birimden (müşteri kaydı ??
 * katalog varsayılanı) FARKLI olan göstergeler — kurulum bunları 409 `KPI_UNIT_IN_USE` ile
 * reddedeceği için adım satırda önceden uyarır.
 */
export function inUseConflicts(
  rows: readonly Pick<UnitFamilyRow, "metrics">[],
  inUse: ReadonlySet<string>,
): Set<string> {
  const out = new Set<string>();
  for (const row of rows) {
    for (const m of row.metrics) {
      if (m.clientUnit === null && inUse.has(m.metricId) && !same(m.unit, m.defaultUnit))
        out.add(m.metricId);
    }
  }
  return out;
}

/**
 * Aile kontrolünde seçili görünecek seçenek: `choice`'un normalize eşi; seçenekler arasında yoksa
 * (ör. müşteri kayıtlı birimi ortak kümede değil) ilk seçenek — kontrol hiçbir zaman boş kalmaz.
 */
export function familySelectedOption(row: Pick<UnitFamilyRow, "options" | "choice">): string {
  return row.options.find((o) => same(o, row.choice)) ?? row.options[0] ?? "";
}

function dedupe(units: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of units) {
    const u = raw.trim();
    const k = keyOf(u);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(u);
  }
  return out;
}

function optionsOf(m: KpiMetric): string[] {
  const d = m.defaultUnit?.trim() ?? "";
  const neighbours = d ? [...reachableUnits(d), ...currencyScaleUnits(d)] : [];
  return dedupe([d, ...m.unitVariants.filter((v) => v.trim() !== ""), ...neighbours]);
}

/**
 * Adıma girer mi: türetilmiş değil ∧ kırılım üyesi/grubu değil (üyenin birimi üstten — tercih
 * gönderilirse fork 422 `unit_change_on_breakdown_child`) ∧ (varyantlı ∨ çevrilebilir komşusu var).
 */
export function isUnitPreferenceCandidate(m: KpiMetric): boolean {
  if (m.isDerived || m.dimensionMember || m.nodeKind === "breakdown_group") return false;
  return optionsOf(m).length > 1;
}

function familyOf(m: KpiMetric): { key: string; label: string } {
  const d = m.defaultUnit?.trim() ?? "";
  if (d && currencyBaseOf(d) !== null) {
    return { key: "currency", label: KPI_UNIT_COPY.fork.currencyFamily };
  }
  const fam = d ? unitFamilyOf(d) : null;
  if (fam) {
    return { key: fam, label: KPI_UNIT_FAMILIES.find((f) => f.key === fam)?.label ?? fam };
  }
  return { key: `metric:${m.id}`, label: m.label };
}

function withExceptions(row: Omit<UnitFamilyRow, "exceptions">): UnitFamilyRow {
  return {
    ...row,
    exceptions: row.metrics
      .filter((m) => !same(m.unit, row.choice))
      .map((m) => ({ metricId: m.metricId, unit: m.unit })),
  };
}

function initialChoice(metrics: readonly UnitPrefMetric[]): string {
  const first = metrics[0];
  if (!first) return "";
  const allClient = metrics.every((m) => m.clientUnit !== null);
  if (allClient && metrics.every((m) => same(m.clientUnit!, first.clientUnit!))) {
    return first.clientUnit!;
  }
  // Paket ön seçimi varsa aile kısayolu ortak ön seçili birimden başlar (paketsiz yol değişmez)
  if (
    metrics.some((m) => m.packageUnit !== null) &&
    metrics.every((m) => same(m.unit, first.unit))
  ) {
    return first.unit;
  }
  if (metrics.every((m) => same(m.defaultUnit, first.defaultUnit))) return first.defaultUnit;
  return first.unit;
}

/**
 * Aile satırları (seçim sırasıyla). `clientUnits`: müşterinin mevcut kalıcı birim kayıtları
 * (metricId → birim). `packageUnits`: işaretli sektör paketlerinin `unit_default`'ları (ilk paket
 * kazanır) — müşteri kaydı yoksa ön seçilir. Aday olmayan göstergeler satıra girmez.
 */
export function buildUnitFamilies(
  selectedMetrics: readonly KpiMetric[],
  clientUnits: ReadonlyMap<string, string>,
  packageUnits: ReadonlyMap<string, string> = new Map(),
): UnitFamilyRow[] {
  const groups = new Map<string, { label: string; metrics: UnitPrefMetric[] }>();
  for (const m of selectedMetrics) {
    if (!isUnitPreferenceCandidate(m)) continue;
    const fam = familyOf(m);
    const defaultUnit = m.defaultUnit?.trim() ?? "";
    const recorded = clientUnits.get(m.id)?.trim() ?? "";
    const clientUnit = recorded === "" ? null : recorded;
    const options = optionsOf(m);
    const pu = packageUnits.get(m.id)?.trim() ?? "";
    const packageUnit =
      clientUnit === null && pu !== "" ? (options.find((o) => same(o, pu)) ?? null) : null;
    const entry: UnitPrefMetric = {
      metricId: m.id,
      label: m.label,
      defaultUnit,
      variants: [...m.unitVariants],
      clientUnit,
      packageUnit,
      options,
      unit: clientUnit ?? packageUnit ?? defaultUnit,
    };
    const g = groups.get(fam.key) ?? { label: fam.label, metrics: [] };
    g.metrics.push(entry);
    groups.set(fam.key, g);
  }
  return [...groups.entries()].map(([key, g]) => {
    const [head, ...rest] = g.metrics;
    const common = (head?.options ?? []).filter((o) =>
      rest.every((m) => m.options.some((x) => same(x, o))),
    );
    return withExceptions({
      key,
      label: g.label,
      metricIds: g.metrics.map((m) => m.metricId),
      options: common,
      choice: initialChoice(g.metrics),
      metrics: g.metrics,
    });
  });
}

/** Aile kısayolu: seçimi alabilen göstergelere uygular; alamayan / müşteri kayıtlı olan istisna kalır. */
export function applyFamilyChoice(
  rows: readonly UnitFamilyRow[],
  familyKey: string,
  unit: string,
): UnitFamilyRow[] {
  return rows.map((row) => {
    if (row.key !== familyKey) return row;
    const metrics = row.metrics.map((m) =>
      m.clientUnit === null && m.options.some((o) => same(o, unit)) ? { ...m, unit } : m,
    );
    return withExceptions({ ...row, choice: unit, metrics });
  });
}

/** Gösterge bazlı istisna düzenlemesi (`null` = katalog varsayılanı). Müşteri kayıtlıda yok sayılır. */
export function setMetricUnit(
  rows: readonly UnitFamilyRow[],
  familyKey: string,
  metricId: string,
  unit: string | null,
): UnitFamilyRow[] {
  return rows.map((row) => {
    if (row.key !== familyKey) return row;
    const metrics = row.metrics.map((m) =>
      m.metricId === metricId && m.clientUnit === null ? { ...m, unit: unit ?? m.defaultUnit } : m,
    );
    return withExceptions({ ...row, metrics });
  });
}

/** "N göstergeye uygulanacak · M istisna" sayıları. */
export function familySummary(row: UnitFamilyRow): { applied: number; exceptions: number } {
  return {
    applied: row.metrics.length - row.exceptions.length,
    exceptions: row.exceptions.length,
  };
}

/** Fork gövdesi `unitPreferences`: yalnız varsayılandan ve mevcut müşteri biriminden farklılar. */
export function toUnitPreferences(
  rows: readonly UnitFamilyRow[],
): { metricId: string; unit: string }[] {
  const out: { metricId: string; unit: string }[] = [];
  for (const row of rows) {
    for (const m of row.metrics) {
      if (m.unit.trim() === "" || same(m.unit, m.defaultUnit)) continue;
      if (m.clientUnit !== null && same(m.unit, m.clientUnit)) continue;
      out.push({ metricId: m.metricId, unit: m.unit.trim() });
    }
  }
  return out;
}
